import { Capacitor, registerPlugin } from '@capacitor/core';

export type JourneyId =
  | 'onboarding'
  | 'customer_authentication'
  | 'live_status_awareness'
  | 'remote_control'
  | 'tariffs_setup'
  | 'schedule_setup'
  | 'historical_data'
  | 'hamburger_menu'
  | 'consent_management';

export type JourneyStep =
  | 'authentication'
  | 'authorization'
  | 'session'
  | 'permission'
  | 'scan'
  | 'wifi'
  | 'local_auth'
  | 'wifi_recovery'
  | 'add_to_site'
  | 'enrollment'
  | 'account'
  | 'tariff'
  | 'schedule'
  | 'status'
  | 'control'
  | 'activity'
  | 'menu';

export type JourneyPriority = 'critical' | 'standard';

type DynatraceTrackEventOptions = {
  journeyId: JourneyId;
  journeyStep: JourneyStep;
  eventName: string;
  outcome?: 'success' | 'failure';
  priority?: JourneyPriority;
  attributesJson?: string;
};

type DynatraceReportErrorOptions = {
  journeyId: JourneyId;
  journeyStep: JourneyStep;
  eventName: string;
  errorMessage: string;
  priority?: JourneyPriority;
  attributesJson?: string;
};

interface DynatraceJourneyPlugin {
  trackEvent(options: DynatraceTrackEventOptions): Promise<void>;
  reportError(options: DynatraceReportErrorOptions): Promise<void>;
}

const DynatraceJourney = registerPlugin<DynatraceJourneyPlugin>('DynatraceJourney');

function isNativePlatform() {
  const platform = Capacitor.getPlatform();
  return platform === 'ios' || platform === 'android';
}

function stringifyAttributes(attributes?: Record<string, unknown>) {
  if (!attributes) return undefined;
  try {
    return JSON.stringify(attributes);
  } catch {
    return undefined;
  }
}

export async function trackDynatraceJourneyEvent(
  options: Omit<DynatraceTrackEventOptions, 'attributesJson'> & { attributes?: Record<string, unknown> }
) {
  if (!isNativePlatform()) return;

  const { attributes, ...nativeOptions } = options;

  try {
    await DynatraceJourney.trackEvent({
      ...nativeOptions,
      attributesJson: stringifyAttributes(attributes),
    });
  } catch {
    // Do not block UX on telemetry failures.
  }
}

export async function trackDynatraceJourneyError(
  options: Omit<DynatraceReportErrorOptions, 'attributesJson'> & { attributes?: Record<string, unknown> }
) {
  if (!isNativePlatform()) return;

  const { attributes, ...nativeOptions } = options;

  try {
    await DynatraceJourney.reportError({
      ...nativeOptions,
      attributesJson: stringifyAttributes(attributes),
    });
  } catch {
    // Do not block UX on telemetry failures.
  }
}
